import type { NestedCut } from '@cutr/nesting';

export type Rect = {
  x: number;
  y: number;
  lengthMM: number;
  widthMM: number;
};

export type Axis = 'x' | 'y';

export type CutResult =
  | { kind: 'part'; ref: string; rect: Rect }
  | { kind: 'offcut'; rect: Rect };

export type Cut = {
  piece: Rect;
  axis: Axis;
  atMM: number;
  results: CutResult[];
};

type CutPosition = { axis: Axis; firstEnd: number; restStart: number };

const EPS_MM = 0.5;

const other = (axis: Axis): Axis => (axis === 'x' ? 'y' : 'x');
const start = (rect: Rect, axis: Axis) => (axis === 'x' ? rect.x : rect.y);
const size = (rect: Rect, axis: Axis) =>
  axis === 'x' ? rect.lengthMM : rect.widthMM;
const end = (rect: Rect, axis: Axis) => start(rect, axis) + size(rect, axis);

const slice = (rect: Rect, axis: Axis, from: number, to: number): Rect =>
  axis === 'x'
    ? { ...rect, x: from, lengthMM: to - from }
    : { ...rect, y: from, widthMM: to - from };

const findPartsCut = ({
  piece,
  parts,
  axis,
  kerfMM,
}: {
  piece: Rect;
  parts: Rect[];
  axis: Axis;
  kerfMM: number;
}): CutPosition | null => {
  const pieceStart = start(piece, axis);
  const pieceEnd = end(piece, axis);
  const minStart = Math.min(...parts.map((part) => start(part, axis)));

  const candidates = [
    ...(parts.length && minStart - pieceStart > kerfMM + EPS_MM
      ? [minStart - kerfMM]
      : []),
    ...parts
      .map((part) => end(part, axis))
      .filter((partEnd) => pieceEnd - partEnd > kerfMM + EPS_MM)
      .filter((partEnd) =>
        parts.every(
          (part) =>
            start(part, axis) >= partEnd - EPS_MM ||
            end(part, axis) <= partEnd + EPS_MM
        )
      ),
  ];
  if (!candidates.length) return null;

  const at = Math.min(...candidates);
  return { axis, firstEnd: at, restStart: at + kerfMM };
};

const lineAxis = (line: NestedCut): Axis =>
  Math.abs(line.x1 - line.x2) < EPS_MM ? 'x' : 'y';

const findStoredCut = ({
  piece,
  bounds,
  lines,
  kerfMM,
}: {
  piece: Rect;
  bounds: Rect;
  lines: NestedCut[];
  kerfMM: number;
}): CutPosition | null => {
  const tolerance = kerfMM / 2 + EPS_MM;
  const inPiece = lines.flatMap((line) => {
    const axis = lineAxis(line);
    const across = other(axis);
    const position = axis === 'x' ? line.x1 : line.y1;
    const [spanFrom, spanTo] =
      axis === 'x' ? [line.y1, line.y2] : [line.x1, line.x2];
    const from = Math.min(spanFrom, spanTo);
    const to = Math.max(spanFrom, spanTo);
    const isInside =
      position > start(piece, axis) + EPS_MM &&
      position < end(piece, axis) - EPS_MM &&
      from >= start(bounds, across) - tolerance &&
      to <= end(bounds, across) + tolerance &&
      from <= start(piece, across) + tolerance &&
      to >= end(piece, across) - tolerance;
    return isInside ? [{ axis, position, level: line.level }] : [];
  });
  if (!inPiece.length) return null;

  const [next] = inPiece.sort(
    (a, b) => a.level - b.level || a.position - b.position
  );
  return {
    axis: next.axis,
    firstEnd: next.position - kerfMM / 2,
    restStart: next.position + kerfMM / 2,
  };
};

export const deriveCuts = <P extends Rect & { ref: string }>({
  sheet,
  parts,
  lines = [],
  kerfMM,
}: {
  sheet: Rect;
  parts: P[];
  lines?: NestedCut[];
  kerfMM: number;
}): Cut[] => {
  const cuts: Cut[] = [];

  const cutFor = (piece: Rect, pieceParts: P[], axis: Axis, bounds: Rect) =>
    lines.length
      ? findStoredCut({ piece, bounds, lines, kerfMM })
      : (findPartsCut({ piece, parts: pieceParts, axis, kerfMM }) ??
        findPartsCut({ piece, parts: pieceParts, axis: other(axis), kerfMM }));

  const resultOf = (
    piece: Rect,
    pieceParts: P[],
    bounds: Rect
  ): CutResult[] => {
    if (cutFor(piece, pieceParts, 'x', bounds)) return [];
    if (!pieceParts.length) return [{ kind: 'offcut', rect: piece }];
    return pieceParts.map((part) => ({
      kind: 'part',
      ref: part.ref,
      rect: part,
    }));
  };

  const walk = (piece: Rect, pieceParts: P[], axis: Axis, bounds = piece) => {
    const cut = cutFor(piece, pieceParts, axis, bounds);
    if (!cut) return;

    const pieceStart = start(piece, cut.axis);
    const middle = (cut.firstEnd + cut.restStart) / 2;
    const isInFirst = (part: P) => end(part, cut.axis) <= middle;
    const firstPiece = slice(piece, cut.axis, pieceStart, cut.firstEnd);
    const firstParts = pieceParts.filter(isInFirst);
    const restPiece = slice(
      piece,
      cut.axis,
      cut.restStart,
      end(piece, cut.axis)
    );
    const restParts = pieceParts.filter((part) => !isInFirst(part));
    const first = {
      piece: firstPiece,
      parts: firstParts,
      bounds: restParts.length ? firstPiece : bounds,
    };
    const rest = {
      piece: restPiece,
      parts: restParts,
      bounds: firstParts.length ? restPiece : bounds,
    };

    cuts.push({
      piece,
      axis: cut.axis,
      atMM: cut.firstEnd - pieceStart,
      results: [
        ...resultOf(first.piece, first.parts, first.bounds),
        ...resultOf(rest.piece, rest.parts, rest.bounds),
      ],
    });
    walk(first.piece, first.parts, other(cut.axis), first.bounds);
    walk(rest.piece, rest.parts, cut.axis, rest.bounds);
  };

  walk(sheet, parts, 'y');
  return cuts;
};
